<div>
  <div class="mb-6">
    <h2 class="text-xl font-semibold mb-2">
      @if (isEdit) {
      {{ 'projectEdit.vestingCategories.editCategory' | translate }}
      } @else {
      {{ 'projectEdit.vestingCategories.addCategory' | translate }}
      }
    </h2>
    <p class="text-gray-400">
      @if (isEdit) {
      {{ 'projectEdit.vestingCategories.updateCategory' | translate }}
      } @else {
      {{ 'projectEdit.vestingCategories.addCategory' | translate }}
      }
    </p>
  </div>

  <form [formGroup]="vestingCategoryForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-6">
    <!-- Category Name Field -->
    <div class="flex flex-col gap-2">
      <label for="categoryName" class="font-medium">{{ 'projectEdit.vestingCategories.category' | translate }}</label>
      <input
        id="categoryName"
        type="text"
        pInputText
        [formControl]="categoryControl"
        [placeholder]="'projectEdit.vestingCategories.categoryPlaceholder' | translate"
        class="w-full"
      />
      @if (categoryControl.invalid && categoryControl.touched) {
      <p-message severity="error">
        {{ getCategoryErrorMessage() }}
      </p-message>
      }
    </div>

    <!-- Wallet Address Field -->
    <div class="flex flex-col gap-2">
      <label for="walletAddress" class="font-medium">{{ 'projectEdit.vestingCategories.walletAddress' | translate }}</label>
      <input
        id="walletAddress"
        type="text"
        pInputText
        [formControl]="walletAddressControl"
        [placeholder]="'projectEdit.vestingCategories.walletAddressPlaceholder' | translate"
        class="w-full font-mono"
      />
      @if (walletAddressControl.invalid && walletAddressControl.touched) {
      <p-message severity="error">
        {{ getWalletAddressErrorMessage() }}
      </p-message>
      }
    </div>

    <!-- Cliff Period Field -->
    <div class="flex flex-col gap-2">
      <label for="cliffPeriod" class="font-medium">{{ 'projectEdit.vestingCategories.cliffPeriod' | translate }}</label>
      <div class="flex gap-2">
        <p-inputNumber
          id="cliffPeriod"
          [formControl]="cliffPeriodValueControl"
          [placeholder]="'projectEdit.vestingCategories.cliffPeriodPlaceholder' | translate"
          [min]="0"
          [step]="1"
          mode="decimal"
          [useGrouping]="false"
          class="flex-1"
        ></p-inputNumber>
        <select [formControl]="cliffPeriodUnitControl" class="p-2 border border-gray-600 bg-surface-800 rounded text-white">
          <option value="months">months</option>
          <option value="days">days</option>
          <option value="years">years</option>
        </select>
      </div>
      @if (cliffPeriodValueControl.invalid && cliffPeriodValueControl.touched) {
      <p-message severity="error">
        {{ getCliffPeriodErrorMessage() }}
      </p-message>
      }
    </div>

    <!-- Vesting Duration Field -->
    <div class="flex flex-col gap-2">
      <label for="vestingDuration" class="font-medium">{{ 'projectEdit.vestingCategories.vestingDuration' | translate }}</label>
      <div class="flex gap-2">
        <p-inputNumber
          id="vestingDuration"
          [formControl]="vestingDurationValueControl"
          [placeholder]="'projectEdit.vestingCategories.vestingDurationPlaceholder' | translate"
          [min]="1"
          [step]="1"
          mode="decimal"
          [useGrouping]="false"
          class="flex-1"
        ></p-inputNumber>
        <select [formControl]="vestingDurationUnitControl" class="p-2 border border-gray-600 bg-surface-800 rounded text-white">
          <option value="months">months</option>
          <option value="days">days</option>
          <option value="years">years</option>
        </select>
      </div>
      @if (vestingDurationValueControl.invalid && vestingDurationValueControl.touched) {
      <p-message severity="error">
        {{ getVestingDurationErrorMessage() }}
      </p-message>
      }
    </div>

    <!-- Releasing Frequency Field -->
    <div class="flex flex-col gap-2">
      <label for="releasingFrequency" class="font-medium">{{ 'projectEdit.vestingCategories.releasingFrequency' | translate }}</label>
      <input
        id="releasingFrequency"
        type="text"
        pInputText
        [formControl]="releasingFrequencyControl"
        [placeholder]="'projectEdit.vestingCategories.releasingFrequencyPlaceholder' | translate"
        class="w-full"
      />
      @if (releasingFrequencyControl.invalid && releasingFrequencyControl.touched) {
      <p-message severity="error">
        {{ getReleasingFrequencyErrorMessage() }}
      </p-message>
      }
    </div>

    <!-- TGE Unlock Field -->
    <div class="flex flex-col gap-2">
      <label for="tgeUnlock" class="font-medium">{{ 'projectEdit.vestingCategories.tgeUnlock' | translate }}</label>
      <p-inputNumber
        id="tgeUnlock"
        [formControl]="tgeUnlockControl"
        [placeholder]="'projectEdit.vestingCategories.tgeUnlockPlaceholder' | translate"
        [min]="0"
        [max]="100"
        [step]="1"
        mode="decimal"
        [useGrouping]="false"
        suffix="%"
        class="w-full"
      ></p-inputNumber>
      @if (tgeUnlockControl.invalid && tgeUnlockControl.touched) {
      <p-message severity="error">
        {{ getTgeUnlockErrorMessage() }}
      </p-message>
      }
    </div>

    <!-- First Claim Available Field -->
    <div class="flex flex-col gap-2">
      <label for="firstClaim" class="font-medium">{{ 'projectEdit.vestingCategories.firstClaim' | translate }}</label>
      <div class="flex gap-2">
        <p-inputNumber
          id="firstClaim"
          [formControl]="firstClaimValueControl"
          [placeholder]="'projectEdit.vestingCategories.firstClaimPlaceholder' | translate"
          [min]="0"
          [step]="1"
          mode="decimal"
          [useGrouping]="false"
          class="flex-1"
        ></p-inputNumber>
        <select [formControl]="firstClaimUnitControl" class="p-2 border border-gray-600 bg-surface-800 rounded text-white">
          <option value="months">months</option>
          <option value="month">month</option>
          <option value="days">days</option>
          <option value="years">years</option>
        </select>
        <select [formControl]="firstClaimTimingControl" class="p-2 border border-gray-600 bg-surface-800 rounded text-white">
          <option value="after TGE">after TGE</option>
          <option value="before TGE">before TGE</option>
        </select>
      </div>
      @if (firstClaimValueControl.invalid && firstClaimValueControl.touched) {
      <p-message severity="error">
        {{ getFirstClaimErrorMessage() }}
      </p-message>
      }
    </div>

    <!-- Action Buttons -->
    <div class="flex flex-col md:flex-row gap-4 mt-6">
      <p-button
        type="submit"
        [label]="
          isEdit
            ? ('projectEdit.vestingCategories.updateCategory' | translate)
            : ('projectEdit.vestingCategories.addCategory' | translate)
        "
        [disabled]="vestingCategoryForm.invalid"
      />

      <p-button
        type="button"
        [label]="'projectEdit.cancel' | translate"
        severity="secondary"
        [outlined]="true"
        (onClick)="onCancel()"
      />
    </div>
  </form>
</div>